import { useEffect, useMemo, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertCircle, Loader2 } from 'lucide-react';

export interface EmbeddedCheckoutModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	clientSecret: string | null;
	publishableKey: string;
	title?: string;
	onComplete?: () => void;
}

export function EmbeddedCheckoutModal({
	open,
	onOpenChange,
	clientSecret,
	publishableKey,
	title = 'Checkout',
	onComplete,
}: EmbeddedCheckoutModalProps) {
	const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
		if (!publishableKey) return null;
		return loadStripe(publishableKey);
	}, [publishableKey]);

	const [completed, setCompleted] = useState(false);

	useEffect(() => {
		if (open) setCompleted(false);
	}, [open]);

	const handleComplete = () => {
		setCompleted(true);
		onComplete?.();
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg" showCloseButton={true}>
				<DialogHeader className="p-4">
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>

				<div className="min-h-[420px] px-4 pb-4">
					{!publishableKey && (
						<div className="flex flex-col items-center gap-3 py-10 text-center">
							<AlertCircle size={28} className="text-destructive" />
							<p className="text-base text-destructive">
								Stripe publishable key is not configured. Set
								VITE_STRIPE_PUBLISHABLE_KEY in your environment.
							</p>
							<Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
								Close
							</Button>
						</div>
					)}

					{publishableKey && !clientSecret && !completed && (
						<div className="flex min-h-[360px] flex-col items-center justify-center gap-3">
							<Loader2 size={24} className="animate-spin text-text-3" />
							<p className="text-base text-text-3">Preparing secure checkout…</p>
						</div>
					)}

					{publishableKey && clientSecret && stripePromise && !completed && (
						<EmbeddedCheckoutProvider
							key={clientSecret}
							stripe={stripePromise}
							options={{ clientSecret, onComplete: handleComplete }}
						>
							<EmbeddedCheckout />
						</EmbeddedCheckoutProvider>
					)}

					{completed && (
						<div className="flex flex-col items-center gap-4 py-10 text-center">
							<div className="text-lg font-medium text-text-1">Payment confirmed</div>
							<p className="text-base text-text-3">Your subscription is active.</p>
							<Button size="sm" onClick={() => onOpenChange(false)}>
								Close
							</Button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
